import React from 'react';
import Image from 'next/image';

import { LibraryIcon, LibraryIconSymbol } from '@repo/icons/library-icon';

import { Icon } from '~/components/ui/icon';
import { cn } from '~/lib/utils';
import { formatCurrency } from '~/utils/number';

interface ProgramUnitSelect {
  cost: number;
  hospitalNeed: number;
  icon: string;
  image: string;
  onQuantityUpdate: (op: string, amount: number) => void;
  onUnitAmountChange: (unitTotal: number, fullySponsored: boolean) => void;
  step: number;
  title: string;
  unitDescription: string;
}

export function ProgramUnitSelect({
  title,
  cost,
  unitDescription,
  step,
  onQuantityUpdate,
  hospitalNeed,
  icon,
  image,
  onUnitAmountChange,
}: ProgramUnitSelect) {
  const [total, setTotal] = React.useState(0);

  const handleIncreaseTotal = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();

      const newTotal = total + step;
      setTotal(newTotal);

      const fullySponsored = hospitalNeed === 0 ? false : hospitalNeed - newTotal <= 0;
      onQuantityUpdate('add', cost * step);
      onUnitAmountChange(newTotal, fullySponsored);
    },
    [cost, hospitalNeed, onQuantityUpdate, onUnitAmountChange, step, total],
  );

  const handleDecreaseTotal = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();

      const newTotal = total - step;
      if (newTotal < 0) return false;
      setTotal(newTotal);
      const fullySponsored = hospitalNeed === 0 ? false : hospitalNeed - newTotal <= 0;

      onQuantityUpdate('subtract', cost * step);
      onUnitAmountChange(newTotal, fullySponsored);
    },
    [cost, hospitalNeed, onQuantityUpdate, onUnitAmountChange, step, total],
  );

  const handleFullySponsor = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();

      const newTotal = total + hospitalNeed - total;
      setTotal(newTotal);

      const fullySponsored = hospitalNeed === 0 ? false : hospitalNeed - newTotal <= 0;
      onQuantityUpdate('add', cost * (hospitalNeed - total));
      onUnitAmountChange(newTotal, fullySponsored);
    },
    [cost, hospitalNeed, onQuantityUpdate, onUnitAmountChange, total],
  );

  return (
    <div className='flex flex-col items-center text-center'>
      <div className='leading-0 relative mb-4 w-32 lg:w-36'>
        <Image alt={title} className='h-auto max-w-full rounded-full' height={152} src={image} width={152} />
        {icon && (
          <div className='bg-yellow absolute right-0 top-2/4 flex h-20 w-20 -translate-y-2/4 translate-x-2/4 items-center justify-center rounded-full'>
            <LibraryIcon className='max-h-10 w-12 text-white' use={icon as LibraryIconSymbol} />
          </div>
        )}
      </div>
      <h4 className='title-sm mb-2'>{title}</h4>
      <div className='text-muted-foreground mb-4'>
        {formatCurrency(cost)} {unitDescription}
        {step > 1 && (
          <>
            <br />({step} minimum)
          </>
        )}
      </div>
      <div className='text-blue mt-auto flex items-center justify-between'>
        <button
          className='border-blue focus:outline-hidden active:bg-blue h-8 w-8 appearance-none rounded-full border-2 bg-white text-2xl active:text-white'
          onClick={handleDecreaseTotal}
        >
          -
        </button>
        <span className='font-greycliff-extrabold w-20 text-2xl '>{total}</span>
        <input name='vr' type='hidden' value={total} />
        <button
          className='border-blue focus:outline-hidden active:bg-blue h-8 w-8 appearance-none rounded-full border-2 bg-white text-2xl active:text-white'
          onClick={handleIncreaseTotal}
        >
          +
        </button>
      </div>
      {hospitalNeed > 0 && (
        <div>
          <button
            className={cn(
              'font-greycliff-extrabold focus:outline-hidden mt-6 flex h-8 items-center justify-center rounded-2xl pl-5 text-xs uppercase',
              total && hospitalNeed - total === 0
                ? 'border-blue text-blue border border-solid pr-5'
                : 'bg-muted text-muted-foreground',
            )}
            onClick={handleFullySponsor}
          >
            {total && hospitalNeed - total === 0 ? (
              <>
                <Icon className='w-4' use='circle-check' />
                Sponsored
              </>
            ) : (
              <>
                <span className='w-20'>{hospitalNeed - total} needed</span>{' '}
                <span className='bg-blue h-8 rounded-full px-5 leading-8 text-white'>Fully Sponsor</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
